/**
 * URL hash scroll handler
 * Scrolls to sections with matching data-anchor-id attribute
 */
export default class UrlScroll {
    private static scrollAttempts = 0;
    private static readonly MAX_ATTEMPTS = 50;
    private static lastScrollPosition = 0;
    private static stableCount = 0;

    /**
     * Initialize anchor scroll functionality
     */
    static init(): void {
        // Disable browser's automatic scroll restoration
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }

        // Reset scroll position immediately if there's a hash
        if (window.location.hash) {
            window.scrollTo(0, 0);
        }

        window.addEventListener('load', () => {
            if (window.location.hash) {
                window.scrollTo(0, 0);
            }

            // Start attempting to scroll
            this.attemptScroll();
        });

        window.addEventListener('hashchange', () => {
            this.scrollAttempts = 0;
            this.lastScrollPosition = 0;
            this.stableCount = 0;
            this.scrollToAnchor();
        });
    }

    /**
     * Attempt to scroll multiple times until layout is stable
     */
    private static attemptScroll(): void {
        this.scrollAttempts++;

        if (this.scrollAttempts > this.MAX_ATTEMPTS) {
            return;
        }

        const targetPosition = this.getTargetPosition();

        if (targetPosition === null) {
            // Element not found, try again
            setTimeout(() => this.attemptScroll(), 200);
            return;
        }

        // Check if position is stable (hasn't changed for 3 attempts on mobile)
        const positionChanged = Math.abs(targetPosition - this.lastScrollPosition) > 10;

        if (positionChanged) {
            this.stableCount = 0;
            this.lastScrollPosition = targetPosition;

            // Use instant scroll while layout is still changing
            window.scrollTo({
                top: targetPosition,
                behavior: 'auto',
            });

            setTimeout(() => this.attemptScroll(), 200);
        } else {
            this.stableCount++;

            // Position is stable - do final smooth scroll (wait for 3 stable checks)
            if (this.stableCount === 3) {
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });
                return;
            }

            setTimeout(() => this.attemptScroll(), 200);
        }
    }

    /**
     * Get target scroll position for current hash
     * @returns target position or null if element not found
     */
    private static getTargetPosition(): number | null {
        const hash = window.location.hash;

        if (!hash) {
            return null;
        }

        const anchorId = hash.substring(1);
        const targetElement = document.querySelector<HTMLElement>(`[data-anchor-id="${anchorId}"]`);

        if (!targetElement) {
            return null;
        }

        return targetElement.getBoundingClientRect().top + window.pageYOffset;
    }

    /**
     * Scroll to element based on current URL hash
     * @returns true if scroll was performed
     */
    private static scrollToAnchor(): boolean {
        const targetPosition = this.getTargetPosition();

        if (targetPosition === null) {
            return false;
        }

        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
        });

        return true;
    }
}
